/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { FieldArray, Formik } from 'formik'
import { Button, FormGroup, Collapse } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { useServiceContext } from '../../root'
import { Select } from '../../components/Select'
import { InfiniteScrollSelect } from '../../components/InfiniteScrollSelect'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import CustomErrorMessage from '../../components/shared/ErrorMessage'
import { FsiLifecycleState, FsiType } from '../../constants/fsi'
import Field from '../../components/shared/Field'
import Validation from '../../lib/Validation'
import usePropertySearchService from '../../hooks/usePropertySearchService'
import PropertyFields from './PropertyFields'
import { PROPERTY_SEARCH_ROW_CONDITION_VALUE, metadataCriteriaEmptyRow } from './constants'
import { TYPE } from '../../screens/settings/properties/Actions/EditFormConstants'
import { USER_ROLE } from '../../constants/user'
import Loader from '../../components/shared/Loader'
import SelectFolderField from '~/components/fsi-picker/SelectFolderField'

// Simple label component to replace ControlLabel (react-bootstrap v2 doesn't have it)
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label
    className={`control-label ${className || ''}`}
    style={{
      display: 'inline-block',
      maxWidth: '100%',
      marginBottom: '5px',
      fontWeight: 700,
      fontSize: '14px'
    }}
  >
    {children}
  </label>
)

const defaultFilterValues = {
  fsiTypeTemplate: null,
  tags: [],
  lifecycleState: null,
  format: null,
  daysBeforeExpiration: '',
  filenameCondition: null,
  filename: '',
  folder: null,
  qcReviewStatusList: []
}

interface PropertySearchFilterProps {
  filterId?: string | number
  handleFilterStatus?: (status: boolean) => void
  persistenceKey?: string
  properties?: string[]
  defaultFilter?: any
}

interface FilterValues {
  fsiTypeTemplate: any
  tags: any[]
  lifecycleState: string | null
  format: string | null
  daysBeforeExpiration: string | number
  filenameCondition: string | null
  filename: string
  folder: any
  qcReviewStatusList: string[]
  metadataCriteria: any[]
  [key: string]: any
}

interface Options {
  state: Array<{ value: string; label: string }>
  filenameConditions: Array<{ value: string; label: string }>
  qcReviewStatus: Array<{ value: string; label: string }>
  format?: Array<{ value: string; label: string; hasBottomSeparator?: boolean }>
}

const PropertySearchFilter: React.FC<PropertySearchFilterProps> = ({ filterId, handleFilterStatus }) => {
  const context = useServiceContext()
  const { assembleFiltersToFormValues } = usePropertySearchService()
  const tableData = useRcmTableData()
  const { t: translate } = useTranslation()
  const { auth } = context.service
  const { api } = context

  const featuresInfo = context?.service?.init?.getFeaturesInfo?.() || {}
  const { qcReview: { qcReviewEnabled } = { qcReviewEnabled: false }, isEnableDocDetailsExpirationDate = false } =
    featuresInfo

  const [initialFilter, setInitialFilter] = useState<FilterValues>({
    ...defaultFilterValues,
    metadataCriteria: [{ ...metadataCriteriaEmptyRow }]
  })
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<Options>({
    state: [
      FsiLifecycleState.EFFECTIVE,
      FsiLifecycleState.OBSOLETE,
      ...(isEnableDocDetailsExpirationDate ? [FsiLifecycleState.HELPER_EXPIRING, FsiLifecycleState.EXPIRED] : []),
      FsiLifecycleState.REJECTED
    ].map((i) => {
      return { value: i, label: translate('STATE_' + i) }
    }),
    filenameConditions: [
      { value: 'EQUALS', label: translate('OPTION_EQUALS') },
      { value: 'CONTAINS', label: translate('OPTION_CONTAINS') }
    ],
    qcReviewStatus: [
      { value: 'NA', label: translate('QC_STATUS_NA') },
      { value: 'Passed', label: translate('QC_STATUS_PASS') },
      !_.includes([USER_ROLE.AUDITOR, USER_ROLE.ETMF_SPONSOR], auth.getRole()) && {
        value: 'Failed',
        label: translate('QC_STATUS_FAIL')
      },
      auth.getRole() !== USER_ROLE.ETMF_SPONSOR && {
        value: 'Remediated',
        label: translate('QC_STATUS_REMEDIATED')
      }
    ].filter((item) => item && Object.keys(item)?.length !== 0) as Array<{
      value: string
      label: string
    }>
  })

  const loadExistingFilter = async () => {
    const filtersApi = await import('../../services/api/api.filters')
    const data = await filtersApi.getById(Number(filterId))
    let parsedFilter = typeof data.filter === 'string' ? JSON.parse(data.filter) : data.filter

    if (!parsedFilter.filter) {
      parsedFilter = { filter: parsedFilter, meta: {} }
    }
    return parsedFilter
  }

  useEffect(() => {
    setIsLoading(true)
    ;(async () => {
      const mimeTypeCategories = await context.api.files.getMimeTypeCategories()
      setOptions((prevState) => {
        return {
          ...prevState,
          format: [
            { value: FsiType.DOCUMENT, label: translate('FSI_TYPE_DOCUMENT') },
            { value: FsiType.REFERENCE, label: translate('FSI_TYPE_REFERENCE') },
            {
              value: FsiType.PLACEHOLDER,
              label: translate('FSI_TYPE_PLACEHOLDER'),
              hasBottomSeparator: true
            },
            ...(Array.isArray(mimeTypeCategories) ? mimeTypeCategories : []).map((i: string) => {
              return { value: i, label: translate(i) }
            })
          ]
        }
      })

      if (parseInt(String(filterId)) > 0) {
        const filterResults = await loadExistingFilter()
        const newFilter = await assembleFiltersToFormValues(filterResults.filter)
        if (!newFilter?.metadataCriteria || newFilter.metadataCriteria.length === 0) {
          _.extend(newFilter, { metadataCriteria: [{ ...metadataCriteriaEmptyRow }] })
        }
        setInitialFilter(newFilter as FilterValues)
        doFilter(newFilter as FilterValues)
      } else {
        setInitialFilter({
          ...defaultFilterValues,
          metadataCriteria: [{ ...metadataCriteriaEmptyRow }]
        })
      }
      setIsLoading(false)
    })()
  }, [filterId])

  const doFilter = (values: FilterValues) => {
    tableData.applyFilter({ ...values })
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <style>
          {`
            .filter-wrapper input::placeholder,
            .filter-wrapper input::-webkit-input-placeholder,
            .filter-wrapper input::-moz-placeholder,
            .filter-wrapper input:-ms-input-placeholder {
              font-size: 12px;
            }
            .filter-wrapper .Select-placeholder,
            .filter-wrapper .react-select__placeholder {
              font-size: 12px;
            }
          `}
        </style>
        <div className="filter-wrapper">
          <Loader blocking={isLoading}>
            <div className="row">
              <div className="col-sm-12 col-lg-12">
                <Formik
                  initialValues={initialFilter}
                  innerRef={(el: any) => {
                    context.store.formRef = el
                  }}
                  validate={(values) => {
                    const errors: any = {
                      ...(values.filenameCondition && !values.filename
                        ? { filename: Validation.general.mixed.required }
                        : null),
                      ...(values.filename && !values.filenameCondition
                        ? { filenameCondition: Validation.general.mixed.required }
                        : null),
                      ...(values.lifecycleState === FsiLifecycleState.HELPER_EXPIRING
                        ? {
                            ...(!values.daysBeforeExpiration
                              ? { daysBeforeExpiration: Validation.general.mixed.required }
                              : null),
                            ...(values.daysBeforeExpiration && Number(values.daysBeforeExpiration) > 365
                              ? {
                                  daysBeforeExpiration: 'FORM_VALIDATION_ERROR_MAX_NO_OF_DAYS_365'
                                }
                              : null),
                            ...(values.daysBeforeExpiration && Number(values.daysBeforeExpiration) < 1
                              ? {
                                  daysBeforeExpiration: 'FORM_VALIDATION_ERROR_MIN_NO_OF_DAYS_1'
                                }
                              : null)
                          }
                        : null)
                    }

                    const metadataCriteriaErrors: any[] = []
                    _.forEach(values.metadataCriteria, (item, index) => {
                      if (item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE) {
                        if (
                          item.property.type === TYPE.DATETIME &&
                          _.includes(
                            [
                              PROPERTY_SEARCH_ROW_CONDITION_VALUE.NEXT_N_DAYS,
                              PROPERTY_SEARCH_ROW_CONDITION_VALUE.PREVIOUS_N_DAYS
                            ],
                            item.condition
                          )
                        ) {
                          if (item.value > 5000) {
                            metadataCriteriaErrors[index] = {
                              value: translate('FORM_VALIDATION_ERROR_MAX_VALUE_IS', {
                                value: 5000
                              })
                            }
                          } else if (item.value < 0) {
                            metadataCriteriaErrors[index] = {
                              value: translate('FORM_VALIDATION_ERROR_MIN_VALUE_IS', { value: 0 })
                            }
                          } else if (item.value === '') {
                            metadataCriteriaErrors[index] = {
                              value: Validation.general.mixed.required
                            }
                          }
                        } else if (item.property.type === TYPE.NUMBER) {
                          if (item.value === '') {
                            metadataCriteriaErrors[index] = {
                              value: Validation.general.mixed.required
                            }
                          }
                        } else if (
                          !item.value ||
                          (Array.isArray(item.value) && item.value.length === 0) ||
                          (typeof item.value === 'string' && !item.value.trim())
                        ) {
                          metadataCriteriaErrors[index] = {
                            value: Validation.general.mixed.required
                          }
                        }
                      }
                    })

                    if (metadataCriteriaErrors.length) {
                      errors.metadataCriteria = metadataCriteriaErrors
                    }

                    return errors
                  }}
                  enableReinitialize={true}
                  onSubmit={(values) => {
                    doFilter(values)
                  }}
                >
                  {({ values, setFieldValue, handleSubmit, resetForm }) => {
                    return (
                      <>
                        <div className="row">
                          <div className="col-sm-4">
                            <FormGroup controlId="aa29abb5-a5a7-40e2-b7c0-353a6e23c72a" className="mb-3">
                              <ControlLabel>{translate('PROPERTIES')}</ControlLabel>
                              <FieldArray name="metadataCriteria">
                                {(arrayHelpers) => (
                                  <PropertyFields arrayHelpers={arrayHelpers} values={values.metadataCriteria} />
                                )}
                              </FieldArray>
                            </FormGroup>
                            <FormGroup controlId="f336ea33-ceb3-44b8-b9ea-6192db229cd3" className="mb-3">
                              <ControlLabel>{translate('TAG_FILTER')}</ControlLabel>
                              <InfiniteScrollSelect
                                placeholder={translate('SELECT_TAG(S)_PLACEHOLDER')}
                                name="tags"
                                value={values.tags}
                                asyncLoadFn={async (search: string, page: number) => {
                                  const response: any = await context.api.tags.getOptionsForAsyncPicker(search, page)

                                  // Tags API returns raw Tag objects with {id, name}, need to format them
                                  const formattedOptions = (response.options || []).map((item: any) => ({
                                    value: item.id,
                                    label: item.name,
                                    type: item.type,
                                    propertyType: item.propertyType
                                  }))

                                  return {
                                    options: formattedOptions,
                                    hasMore: response.hasMore
                                  }
                                }}
                                onChange={(option: any) => {
                                  setFieldValue('tags', option)
                                }}
                                isMulti
                                searchable
                                id="f336ea33-ceb3-44b8-b9ea-6192db229cd3"
                              />
                            </FormGroup>
                            {qcReviewEnabled ? (
                              <FormGroup controlId="k836ea33-peb3-4468-b9ea-6192dk229cd3" className="mb-3">
                                <ControlLabel>{translate('QC_REVIEW_STATUS_FILTER_MULTI')}</ControlLabel>
                                <Select
                                  placeholder={translate('SELECT_MULTIPLE_QC_STATUS_PLACEHOLDER')}
                                  name="qcReviewStatusList"
                                  onChange={(option: any) => {
                                    setFieldValue('qcReviewStatusList', option ? option.map((o: any) => o.value) : [])
                                  }}
                                  value={values?.qcReviewStatusList}
                                  options={options.qcReviewStatus}
                                  isMulti
                                  searchable
                                  id="k836ea33-peb3-4468-b9ea-6192dk229cd3"
                                />
                              </FormGroup>
                            ) : null}
                          </div>
                          <div className="col-sm-4">
                            <FormGroup controlId="23d240a2-dde2-438d-8fea-74009b9aa1c3" className="mb-3">
                              <ControlLabel>{translate('REPORTS_FILTER_LABEL_FILENAME')}</ControlLabel>
                              <Select
                                placeholder={translate('OPTION_SELECT_CONDITION')}
                                name="filenameCondition"
                                onChange={(option: any) => {
                                  setFieldValue('filenameCondition', option ? option.value : null)
                                }}
                                value={values.filenameCondition}
                                options={options.filenameConditions}
                                searchable
                                id="23d240a2-dde2-438d-8fea-74009b9aa1c3"
                              />
                              <CustomErrorMessage name="filenameCondition" />
                            </FormGroup>
                            {/* Add gap below dropdown */}
                            <div style={{ marginBottom: '16px' }} />
                            <FormGroup controlId="52bf7459-c756-4504-a987-88311e5c24b7" className="mb15">
                              <Field type="text" autoFocus name="filename" id="52bf7459-c756-4504-a987-88311e5c24b7" />
                              <CustomErrorMessage name="filename" />
                            </FormGroup>

                            <FormGroup controlId="3862984d-7f6e-45bc-8189-5495e57655ea" className="mb-3">
                              <ControlLabel>{translate('REPORTS_FILTER_LABEL_FOLDER')}</ControlLabel>
                              <SelectFolderField
                                value={values.folder}
                                onSelect={(fsi) => {
                                  if (fsi && fsi.id) {
                                    setFieldValue('folder', fsi)
                                  }
                                }}
                                options={{
                                  ...(auth.getRole() === USER_ROLE.ETMF_SITE_USER
                                    ? { isSiteUser: true, showRC: false }
                                    : null),
                                  ...(auth.getRole() === USER_ROLE.ETMF_STUDY_USER
                                    ? { isStudyUser: true, showRC: false }
                                    : null),
                                  ...(_.includes([USER_ROLE.ETMF_SPECIALIST, USER_ROLE.ETMF_STUDY_USER], auth.getRole())
                                    ? { isEtmfUser: true }
                                    : null)
                                }}
                              />
                            </FormGroup>
                          </div>
                          <div className="col-sm-4">
                            <FormGroup controlId="c87cf267-fe7d-4db6-a244-a3259bc302b7" className="mb-3">
                              <ControlLabel>{translate('REPORTS_FILTER_LABEL_FORMAT')}</ControlLabel>
                              <Select
                                placeholder={translate('OPTION_SELECT_FORMAT')}
                                name="format"
                                onChange={(option: any) => {
                                  setFieldValue('format', option ? option.value : null)
                                }}
                                value={values.format}
                                options={options.format}
                                multi={false}
                                searchable
                                id="c87cf267-fe7d-4db6-a244-a3259bc302b7"
                              />
                            </FormGroup>

                            <FormGroup controlId="480c8810-2532-47e1-ac6d-d41fc4b504ff" className="mb-3">
                              <ControlLabel>{translate('REPORTS_FILTER_LABEL_DOCUMENT_TYPE')}</ControlLabel>
                              <InfiniteScrollSelect
                                placeholder={translate('SELECT_DOC_TYPE_PLACEHOLDER')}
                                name="fsiTypeTemplate"
                                value={values.fsiTypeTemplate}
                                asyncLoadFn={async (search: string, page: number) => {
                                  const response: any = await context.api.documentTypes.getOptionsForAsyncPicker(
                                    search,
                                    page
                                  )

                                  // API already returns formatted response with {options: [{value, label}], hasMore}
                                  return {
                                    options: response.options || [],
                                    hasMore: response.hasMore || false
                                  }
                                }}
                                onChange={(option: any) => {
                                  setFieldValue('fsiTypeTemplate', option)
                                }}
                                searchable
                                id="480c8810-2532-47e1-ac6d-d41fc4b504ff"
                              />
                            </FormGroup>

                            <FormGroup controlId="3260d430-e112-4997-97ea-3bf215e1693a" className="mb-3">
                              <ControlLabel>{translate('REPORTS_FILTER_LABEL_STATE')}</ControlLabel>
                              <Select
                                placeholder={translate('OPTION_ALL_STATES')}
                                name="lifecycleState"
                                onChange={(option: any) => {
                                  setFieldValue('lifecycleState', option ? option.value : null)
                                }}
                                value={values.lifecycleState}
                                options={options.state}
                                multi={false}
                                showNewOptionAtTop={true}
                                searchable
                                id="3260d430-e112-4997-97ea-3bf215e1693a"
                              />
                            </FormGroup>
                            {values.lifecycleState === FsiLifecycleState.HELPER_EXPIRING && (
                              <FormGroup controlId="cb449261-f10f-4eeb-b403-9a2c1ca516b4" className="mb-3">
                                <ControlLabel>{translate('REPORTS_FILTER_LABEL_DAYS_BEFORE_EXPIRATION')}</ControlLabel>
                                <Field
                                  type="number"
                                  autoFocus
                                  maxLength={365}
                                  minLength={1}
                                  name="daysBeforeExpiration"
                                  id="cb449261-f10f-4eeb-b403-9a2c1ca516b4"
                                />
                                <CustomErrorMessage name="daysBeforeExpiration" />
                              </FormGroup>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'left',
                            gap: '7px',
                            marginTop: '25px'
                          }}
                        >
                          <Button
                            data-action={'filter'}
                            className="btn btn-primary"
                            onClick={() => {
                              _.isFunction(handleFilterStatus) && handleFilterStatus(true)
                              handleSubmit()
                            }}
                            disabled={tableData.isLoading}
                            id="d5b71934-35e2-4461-8af4-a40ac501d833"
                          >
                            {translate('BTN_FILTER')}
                          </Button>
                          <Button
                            data-action={'reset'}
                            className="btn btn-chita btn-link"
                            variant="link"
                            onClick={() => {
                              _.isFunction(handleFilterStatus) && handleFilterStatus(true)
                              setInitialFilter({
                                ...defaultFilterValues,
                                metadataCriteria: [{ ...metadataCriteriaEmptyRow }]
                              })
                              resetForm({
                                values: {
                                  ...defaultFilterValues,
                                  metadataCriteria: [{ ...metadataCriteriaEmptyRow }]
                                }
                              })
                            }}
                            id="1ee65bc6-5724-42fb-af11-542aa0911564"
                          >
                            {translate('BTN_CLEAR_FILTER')}
                          </Button>
                        </div>
                      </>
                    )
                  }}
                </Formik>
              </div>
            </div>
          </Loader>
        </div>
      </div>
    </Collapse>
  )
}

export default PropertySearchFilter
